import { supabase, getUsersByRoles } from "../supabase.config";

const normalizeRecipientIds = (values = []) => {
  const ids = new Set();

  values.forEach((value) => {
    if (!value) return;

    if (typeof value === "string") {
      ids.add(value);
      return;
    }

    if (typeof value === "object") {
      if (value.id) ids.add(value.id);
      else if (value.uid) ids.add(value.uid);
    }
  });

  return Array.from(ids);
};

const extractOrderReference = (order = {}) =>
  order?.tracking_number ||
  order?.order_number ||
  order?.order_id ||
  order?.lrno ||
  order?.orderID ||
  null;

const extractOrderId = (order = {}) => order?.id || null;

export const listNotifications = async (recipientUserId, limit = 20) => {
  if (!recipientUserId) return { data: [], error: null };

  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("recipient_user_id", recipientUserId)
    .order("created_at", { ascending: false })
    .limit(limit);

  return { data: data || [], error };
};

export const markNotificationsRead = async (notificationIds = []) => {
  const ids = normalizeRecipientIds(notificationIds);
  if (!ids.length) return { data: [], error: null };

  const { data, error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .in("id", ids)
    .is("read_at", null)
    .select();

  return { data: data || [], error };
};

export const subscribeToNotifications = (recipientUserId, onInsert) => {
  if (!recipientUserId) {
    return { unsubscribe: () => {} };
  }

  const channel = supabase
    .channel(`notifications:${recipientUserId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `recipient_user_id=eq.${recipientUserId}`,
      },
      (payload) => {
        onInsert?.(payload.new);
      }
    )
    .subscribe();

  return {
    unsubscribe: () => {
      supabase.removeChannel(channel);
    },
  };
};

export const createUserNotifications = async ({
  recipientUserIds = [],
  actorUserId = null,
  eventType,
  title,
  message,
  entityType = "system",
  entityId = null,
  order = null,
  metadata = {},
  excludeActor = true,
}) => {
  const recipients = normalizeRecipientIds(recipientUserIds).filter(Boolean);
  const filteredRecipients = excludeActor && actorUserId
    ? recipients.filter((id) => id !== actorUserId)
    : recipients;

  if (!filteredRecipients.length) {
    return { data: [], error: null };
  }

  const rows = filteredRecipients.map((recipientUserId) => ({
    recipient_user_id: recipientUserId,
    actor_user_id: actorUserId,
    event_type: eventType,
    title,
    message,
    entity_type: entityType,
    entity_id: entityId,
    order_reference: extractOrderReference(order || {}),
    metadata,
  }));

  const { data, error } = await supabase
    .from("notifications")
    .insert(rows)
    .select();

  return { data: data || [], error };
};

export const emitOrderNotification = async ({
  actorUserId = null,
  order = {},
  eventType,
  title,
  message,
  recipientUserIds = [],
  metadata = {},
}) => {
  const { data: adminUsers } = await getUsersByRoles(["admin"], "logistics");
  const adminIds = (adminUsers || []).map((user) => user.id);
  const orderUserId = order?.user_id || order?.uid || null;

  return createUserNotifications({
    recipientUserIds: [...adminIds, orderUserId, ...recipientUserIds],
    actorUserId,
    eventType,
    title,
    message,
    entityType: "order",
    entityId: extractOrderId(order),
    order,
    metadata: {
      orderId: extractOrderId(order),
      orderReference: extractOrderReference(order),
      ...metadata,
    },
  });
};
